import type { DropdownState, FocusedElement } from '../state/types';
import { ARIA_LABELS } from '../types';
import { ClearButton } from './ClearButton';
import {
    SymbolPickerMenu,
    SymbolPickerMenuTrigger,
    SymbolPickerMenuContent,
    SymbolPickerMenuItem,
    SymbolPickerMenuSpinner,
} from './SymbolPickerMenu';
import { LocalDate, DateTimeFormatter } from '@js-joda/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@thinkalpha/ui-core/utils';
import IMask from 'imask';
import type { InputMask } from 'imask';
import { useEffect, useRef } from 'react';
import { useEvent } from 'src/hooks/useEvent';

export interface OptionsDatePickerProps {
    value?: string;
    onChange: (date: string) => void;
    onClear?: () => void;
    clearable?: boolean;
    dropdownOpen: boolean;
    dropdown: DropdownState;
    onDropdownSearch: (term: string) => void;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
}

export const OptionsDatePicker = ({
    value,
    onChange,
    onClear,
    clearable,
    dropdownOpen,
    dropdown,
    onDropdownSearch,
    onDropdownActiveIndex,
    onFocus,
}: OptionsDatePickerProps) => {
    // Ref for the input element
    const inputRef = useRef<HTMLInputElement>(null);
    // Ref for the IMask instance
    const maskRef = useRef<InputMask<{ mask: string }> | null>(null);
    // Ref for the dialog element
    const dialogRef = useRef<HTMLDivElement>(null);
    // Ref for the scrollable element in the dropdown
    const virtualScrollRef = useRef<HTMLDivElement>(null);

    // Set up virtualizer
    const virtualizer = useVirtualizer({
        count: dropdown.filteredItems.length,
        getScrollElement: () => virtualScrollRef.current,
        estimateSize: () => 16, // 4rem in pixels
        // Add some overscan for smoother scrolling
        overscan: 5,
    });

    // Set up IMask with pattern blocks
    useEffect(() => {
        if (!inputRef.current) return;

        const mask = IMask(inputRef.current, {
            mask: 'YY-MM-DD',
            lazy: false,
            blocks: {
                YY: {
                    mask: '00',
                    placeholderChar: 'Y',
                },
                MM: {
                    mask: '00',
                    placeholderChar: 'M',
                },
                DD: {
                    mask: '00',
                    placeholderChar: 'D',
                },
            },
        });

        maskRef.current = mask;

        return () => {
            mask.destroy();
            maskRef.current = null;
        };
    }, [onDropdownSearch, onChange]);

    // Force virtualizer to remeasure when dropdown opens
    useEffect(() => {
        if (dropdownOpen && dropdown.filteredItems.length > 0) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                virtualizer.measure();
            });
        }
    }, [dropdownOpen, dropdown.filteredItems.length, virtualizer]);

    // Sync active index with virtualizer when keyboard navigating
    useEffect(() => {
        if (dropdown.activeIndex >= 0 && dropdown.activeIndex < dropdown.filteredItems.length) {
            virtualizer.scrollToIndex(dropdown.activeIndex, {
                align: 'auto',
            });
        }
    }, [dropdown.activeIndex, dropdown.filteredItems.length, virtualizer]);

    const handleInputClick = useEvent((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFocus('expirationDate');
    });

    const handleFocus = useEvent((e: React.FocusEvent) => {
        e.preventDefault();
        e.stopPropagation();
    });

    // blur is allowed to propagate to the parent container
    // unless the blur event comes from focus shifting to the dropdown
    const handleBlur = useEvent((e: React.FocusEvent) => {
        const next = e.relatedTarget;
        if (next && dialogRef.current?.contains(next)) {
            e.stopPropagation();
        }
    });

    // Convert YYMMDD to YYYY-MM-DD format for display
    const formatDisplayDate = (date: string) => {
        if (!date) return '';
        return LocalDate.parse(date, DateTimeFormatter.ofPattern('yyMMdd')).toString();
    };

    // Convert YYMMDD to YY-MM-DD format for input display
    const formatInputDate = (date: string) => {
        if (!date || date.length !== 6) return '';
        return `${date.substring(0, 2)}-${date.substring(2, 4)}-${date.substring(4, 6)}`;
    };

    const handleClearDate = useEvent(() => {
        if (onClear) {
            onClear();
        } else {
            onChange('');
        }
        // Clear the IMask value too
        if (maskRef.current) {
            maskRef.current.value = '';
        }
    });

    // Update IMask value when value prop changes
    useEffect(() => {
        if (maskRef.current) {
            if (value) {
                maskRef.current.value = formatInputDate(value);
            } else {
                maskRef.current.value = '';
            }
        }
    }, [value]);

    return (
        <SymbolPickerMenu open={dropdownOpen}>
            <SymbolPickerMenuTrigger>
                <div className="relative flex h-5 min-w-fit">
                    <input
                        ref={inputRef}
                        type="text"
                        tabIndex={-1}
                        aria-label={ARIA_LABELS.expirationDate}
                        onClick={handleInputClick}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        className={cn(
                            'h-full w-16 bg-transparent px-0 text-xs outline-none dark:border-transparent',
                            maskRef.current?.value === 'YY-MM-DD' && 'text-gray-300',
                        )}
                    />
                    {value && maskRef.current?.value !== 'YY-MM-DD' && clearable && (
                        <ClearButton onClick={handleClearDate} />
                    )}
                </div>
            </SymbolPickerMenuTrigger>
            <SymbolPickerMenuContent className="w-24 p-0" ref={dialogRef}>
                {dropdown.state === 'loading' ? (
                    <div className="h-40 overflow-auto rounded-lg">
                        <SymbolPickerMenuSpinner />
                    </div>
                ) : dropdown.filteredItems.length > 0 ? (
                    <div
                        ref={virtualScrollRef}
                        className={cn(
                            'overflow-auto rounded-b',
                            dropdown.filteredItems.length > 10 ? 'h-40' : 'h-auto',
                        )}
                    >
                        <div
                            style={{
                                height: `${virtualizer.getTotalSize()}px`,
                                width: '100%',
                                position: 'relative',
                            }}
                        >
                            {virtualizer.getVirtualItems().map((virtualItem) => {
                                const date = dropdown.filteredItems[virtualItem.index] as string;

                                return (
                                    <div
                                        key={virtualItem.key}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: `${virtualItem.size}px`,
                                            transform: `translateY(${virtualItem.start}px)`,
                                        }}
                                    >
                                        <SymbolPickerMenuItem
                                            selected={dropdown.activeIndex === virtualItem.index}
                                            onMouseEnter={() => onDropdownActiveIndex(virtualItem.index)}
                                            onSelect={() => onChange(date)}
                                        >
                                            <div className="w-20 flex-none">{formatDisplayDate(date)}</div>
                                        </SymbolPickerMenuItem>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : null}
            </SymbolPickerMenuContent>
        </SymbolPickerMenu>
    );
};
